/** Small helpers shared by the contract screens (C6). */

import { ApiError, type ContractStatusEnum } from "@agencyos/api-client";
import type { BadgeTone } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";

import { fieldErrors } from "../../lib/settings";

export const STATUS_TONE: Record<ContractStatusEnum, BadgeTone> = {
  draft: "default",
  ready: "offer",
  sent: "offer",
  signed: "success",
  cancelled: "danger",
};

export const PERSON_FIELDS = ["full_name", "birth_date", "nationality", "id_number", "phone", "email", "address"] as const;
export type PersonField = (typeof PERSON_FIELDS)[number] | "relation";

/** Refreshes every contracts query after a change (list, detail, templates, clauses). */
export function useRefreshContracts() {
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({
      predicate: (query) => String(query.queryKey[0] ?? "").startsWith("/api/v1/contracts/"),
    });
}

/** The API's own message (already translated), else the first field error, else a fallback. */
export function errorText(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.detail) return error.detail;
  return Object.values(fieldErrors(error))[0] ?? fallback;
}

export function localeOf(language: string): string {
  return language === "en" ? "en-GB" : language;
}

/** Date and time in the agency's time zone (Africa/Douala). */
export function dateTime(iso: string | null | undefined, language: string): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString(localeOf(language), {
    timeZone: "Africa/Douala",
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function dateOnly(iso: string | null | undefined, language: string): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(localeOf(language), { timeZone: "Africa/Douala", dateStyle: "medium" });
}
